import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '@/services';
import type { PaySchedule, RouteAssignment } from '@/types';
import { DEFAULT_PAY_SCHEDULE, getReportPeriod, notifyLiveDataError } from '@/utils';

const MAX_HISTORY_RESULTS = 200;

/**
 * Subscribes to every assignment episode (claimed, unassigned, reassigned
 * away, etc.) the given user has ever had, newest first. This is a personal
 * audit trail, not just "currently held" routes.
 */
export const useMyAssignments = (
  userId: string | undefined,
  periodMonth?: string,
  paySchedule: PaySchedule = DEFAULT_PAY_SCHEDULE,
) => {
  const [assignments, setAssignments] = useState<RouteAssignment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setAssignments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setAssignments([]);
    const assignmentsCollection = collection(db, 'assignments');
    const period = periodMonth ? getReportPeriod(paySchedule, periodMonth) : undefined;
    const q = period
      ? query(
          assignmentsCollection,
          where('userId', '==', userId),
          where('date', '>=', period.startDate),
          where('date', '<', period.endDateExclusive),
          orderBy('date', 'desc'),
        )
      : query(
          assignmentsCollection,
          where('userId', '==', userId),
          orderBy('date', 'desc'),
          limit(MAX_HISTORY_RESULTS),
        );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setAssignments(
          snapshot.docs.map((docSnap) => ({
            ...(docSnap.data() as RouteAssignment),
            id: docSnap.id,
          })),
        );
        setLoading(false);
      },
      (error) => {
        notifyLiveDataError('useMyAssignments', error);
        setLoading(false);
      },
    );
    return unsubscribe;
  }, [userId, periodMonth, paySchedule]);

  return { assignments, loading };
};
